// BUILD-046 task 8.18 (supporting model): the engine behind the My Work host's lists (design "List-state model", "Sequence: caller switch";
// Req 2.7, 2.12, 15.4 - 15.10).
//
// It owns the four lists of listStateModel (myTasks, shared, oversight, team) and runs their requests:
//   load(list)      : request -> fetch -> apply. The answer is tagged (epoch, seq) at request time and applied only if it is still the latest
//                     request of the latest Caller epoch; an answer the client dropped as stale (isStaleOutcome) changes nothing.
//   retry(list)     : the same call. It sends the request for THAT list only (Req 15.6): no other list is requested, none changes.
//   callerChanged() : every list is requested again under the new epoch and everything the previous Caller had is cleared at once; the
//                     lists that have a fetcher are reloaded. An answer still in flight for the previous Caller is discarded (Req 2.7).
// A list without a fetcher stays in `loading` after a Caller change until its view registers one and loads it.
// A fetcher that throws, or answers something that is not a list, is a FAILURE for that list, never an empty list (Req 15.7).
//
// Framework-free so it can be tested without a DOM; useWorkLists.ts wraps it for React. Erasable TypeScript; runtime imports are the model only.

import { LIST_NAMES, initialLists, isStaleOutcome, listsReducer, mergeListItem, requestAllLists, requestList, toListError } from './listStateModel';
import type { ListEvent, ListName, ListsState } from './listStateModel';
// Cycle A (2026-10-21): the silent re-read when the Phoenix day turns (refresh / refreshLoaded below) and the note a failed one leaves.
import { REFRESH_INVALID_ANSWER, canRefresh, refreshedState } from './listRefreshModel';
import type { RefreshNote, RefreshNotes } from './listRefreshModel';

export type ListFetcher<T> = () => Promise<{ items: readonly T[] }>;

export interface ListsControllerDeps {
  /** The current Caller epoch (`workApi.getCallerEpoch`). Read when a request starts, so each answer is tagged with the epoch it began under. */
  getEpoch: () => number;
}

export interface ListsController<T> {
  getState: () => ListsState<T>;
  /** Returns the unsubscribe function. Listeners run after every change. */
  subscribe: (listener: () => void) => () => void;
  /**
   * Sets (or, with null, removes) how a list is fetched. Retry and a Caller change use the fetcher that is registered at that moment.
   * `reloadOnCallerChange: false` (found by the browser run of task 9.39) keeps a Caller change from requesting THIS list on its own: the list is
   * cleared like the others, but its view asks for it again once the new Caller's summary says the view is theirs. The Oversight list uses it,
   * because the previous Caller's fetcher is still registered at the instant of the change and the new Caller may not be an Authorized_Overseer
   * (Req 7.12, 14.21: no Oversight_View data is requested for a Caller whose summary says `canOversee` is false).
   */
  setFetcher: (list: ListName, fetcher: ListFetcher<T> | null, options?: { reloadOnCallerChange?: boolean }) => void;
  load: (list: ListName) => Promise<void>;
  retry: (list: ListName) => Promise<void>;
  /**
   * Replaces one item of a list that holds data by the server's item (a Done, an action). A list that is not holding data, or that does not
   * hold the id, is left alone; the list's phase never changes. Nothing is requested.
   */
  mergeItem: (list: ListName, id: string, item: T) => void;
  callerChanged: () => Promise<void>;
  /**
   * SILENT re-read of one list that holds an answer (data or a genuine empty), through the SAME fetcher and Caller epoch as `load`. Unlike `load` it never enters `loading`: the
   * rows stay on screen until the new ones arrive, then are swapped (same request position, so no older answer can slip in). A failure keeps the rows and leaves a
   * `RefreshNote` for that list (see `getRefreshNotes`); a list that is loading or failed, or has no fetcher, is left alone. An answer that a `load`, a Caller change or a later
   * refresh has overtaken is dropped; one that an item merge (Done) overtook is read again (at most twice more), so a refresh can never undo a merge.
   */
  refresh: (list: ListName) => Promise<void>;
  /** `refresh` for every list that has a fetcher and holds an answer. This is what the Phoenix day turning (midnight, or the tab visible again on a new day) calls. */
  refreshLoaded: () => Promise<void>;
  /** The lists whose last silent re-read failed (their rows are the last ones loaded), or are being retried. The same object until something changes; `subscribe` listeners run when it does. */
  getRefreshNotes: () => RefreshNotes;
}

export function createListsController<T>(deps: ListsControllerDeps): ListsController<T> {
  let state: ListsState<T> = initialLists<T>();
  const fetchers: Partial<Record<ListName, ListFetcher<T>>> = {};
  // Lists a Caller change must not request by itself (see setFetcher); everything else reloads as before.
  const heldOnCallerChange = new Set<ListName>();
  const listeners = new Set<() => void>();
  // The failed (or retrying) silent re-reads, per list, and a counter per list so only the LATEST refresh of a list may apply its answer.
  let notes: RefreshNotes = {};
  const refreshTokens: Record<ListName, number> = { myTasks: 0, shared: 0, oversight: 0, team: 0 };

  function notify(): void {
    for (const listener of [...listeners]) listener();
  }

  function publish(next: ListsState<T>): void {
    if (next === state) return;
    state = next;
    notify();
  }

  function setNote(list: ListName, note: RefreshNote | null): void {
    if (note === null && notes[list] === undefined) return;
    const next: Partial<Record<ListName, RefreshNote>> = { ...notes };
    if (note === null) delete next[list];
    else next[list] = note;
    notes = next;
    notify();
  }

  function clearNotes(): void {
    if (Object.keys(notes).length === 0) return;
    notes = {};
    notify();
  }

  function apply(list: ListName, event: ListEvent<T>): void {
    publish(listsReducer(state, { list, event }));
  }

  async function run(list: ListName, epoch: number, seq: number): Promise<void> {
    const fetcher = fetchers[list];
    if (fetcher === undefined) return;
    let answer: { items: readonly T[] };
    try {
      answer = await fetcher();
    } catch (thrown) {
      // Dropped because the Caller changed: not data and not a failure. The Caller change has already requested this list again.
      if (isStaleOutcome(thrown)) return;
      apply(list, { type: 'failure', epoch, seq, error: toListError(thrown) });
      return;
    }
    apply(list, { type: 'success', epoch, seq, items: answer === null || answer === undefined ? (undefined as unknown as readonly T[]) : answer.items });
  }

  async function load(list: ListName): Promise<void> {
    const started = requestList(state, list, deps.getEpoch());
    publish(started.lists);
    // A load reads the list afresh (and shows its own loading / error): whatever a silent re-read left for this list is superseded.
    setNote(list, null);
    await run(list, started.epoch, started.seq);
  }

  async function refresh(list: ListName, attempt = 0): Promise<void> {
    const fetcher = fetchers[list];
    if (fetcher === undefined) return;
    const startState = state[list];
    const epoch = deps.getEpoch();
    if (!canRefresh(startState) || startState.epoch !== epoch) return;
    const token = ++refreshTokens[list];
    const earlier = notes[list];
    if (earlier !== undefined && !earlier.retrying) setNote(list, { error: earlier.error, retrying: true });
    // This refresh may still speak only while it is the latest refresh of the list and the Caller has not changed.
    const mine = (): boolean => refreshTokens[list] === token && deps.getEpoch() === epoch;
    // A `load` or a Caller change that began after this refresh moved the list's request position: that read is at least as fresh, so this answer is dropped.
    const overtaken = (): boolean => state[list].epoch !== startState.epoch || state[list].seq !== startState.seq || !canRefresh(state[list]);
    let answer: { items: readonly T[] } | null | undefined;
    try {
      answer = await fetcher();
    } catch (thrown) {
      // Dropped because the Caller changed: not data and not a failure.
      if (isStaleOutcome(thrown) || !mine() || overtaken()) return;
      setNote(list, { error: toListError(thrown), retrying: false });
      return;
    }
    if (!mine() || overtaken()) return;
    if (answer === null || answer === undefined || !Array.isArray(answer.items)) {
      setNote(list, { error: REFRESH_INVALID_ANSWER, retrying: false });
      return;
    }
    if (state[list] !== startState) {
      // Same request position but a different list object: an item was merged (a Done, an action) while this read was in flight, so the answer may predate it. Read again.
      if (attempt < 2) return refresh(list, attempt + 1);
      const pending = notes[list];
      if (pending !== undefined && pending.retrying) setNote(list, { error: pending.error, retrying: false });
      return;
    }
    publish({ ...state, [list]: refreshedState(startState, answer.items) });
    setNote(list, null);
  }

  async function refreshLoaded(): Promise<void> {
    await Promise.all(LIST_NAMES.filter((name) => fetchers[name] !== undefined && canRefresh(state[name])).map((name) => refresh(name)));
  }

  async function callerChanged(): Promise<void> {
    const epoch = deps.getEpoch();
    const started = requestAllLists(state, epoch);
    publish(started.lists);
    clearNotes();
    await Promise.all(LIST_NAMES.filter((name) => fetchers[name] !== undefined && !heldOnCallerChange.has(name)).map((name) => run(name, epoch, started.seqs[name])));
  }

  return {
    getState: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    setFetcher(list, fetcher, options) {
      if (fetcher === null) {
        delete fetchers[list];
        heldOnCallerChange.delete(list);
        return;
      }
      fetchers[list] = fetcher;
      if (options?.reloadOnCallerChange === false) heldOnCallerChange.add(list);
      else heldOnCallerChange.delete(list);
    },
    load,
    retry: load,
    mergeItem(list, id, item) {
      const current = state[list];
      const next = mergeListItem(current, id, item);
      if (next !== current) publish({ ...state, [list]: next });
    },
    callerChanged,
    refresh,
    refreshLoaded,
    getRefreshNotes: () => notes,
  };
}
